import { GoogleMap, InfoWindowF } from '@react-google-maps/api';
import type { MutableRefObject, ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

type Marker = {
  lat: number;
  lng: number;
};

type Position = {
  lat: number;
  lng: number;
} | null;

type PlaceId = {
  placeId: string;
} | null;

type GoogleMapProps = {
  center: google.maps.LatLngLiteral;
  zoom: number;
  children: ReactNode;
  onMarkerClick: (markerId: string) => void;
  renderMarkers?: () => (string | JSX.Element | null)[];
  mapButton?: ({
    handleZoomChange,
    handleCenterChange,
  }: {
    handleZoomChange: (newZoom: number) => void;
    handleCenterChange: (newCenter: google.maps.LatLngLiteral) => void;
  }) => JSX.Element;
  onZoomChange?: (realTimeZoom: number) => void;
  markerPositions?: Marker[];
};

const MyGoogleMap: React.FC<GoogleMapProps> = ({
  center,
  zoom,
  children,
  onMarkerClick,
  renderMarkers,
  mapButton,
  onZoomChange,
  markerPositions,
}) => {
  const [currentZoom, setCurrentZoom] = useState<number>(zoom);
  const [currentCenter, setCurrentCenter] = useState<google.maps.LatLngLiteral>(center);
  const [currentPosition, setCurrentPosition] = useState<Position>(null);
  const [placeId, setPlaceId] = useState<PlaceId>(null);
  const [isShow, setShow] = useState(false);

  useEffect(() => {
    if (placeId === null || placeId === undefined) {
      setShow(true);
    } else {
      setShow(false);
    }
  }, [placeId]);

  useEffect(() => {
    onZoomChange?.(currentZoom);
  }, [currentZoom, onZoomChange]);

  const handleZoomChange = (newZoom: number) => {
    setCurrentZoom(newZoom);
  };

  const handleCenterChange = (newCenter: google.maps.LatLngLiteral) => {
    setCurrentCenter(newCenter);
  };

  const mapOptions: google.maps.MapOptions = {
    mapTypeControl: false,
    streetViewControl: false,
    zoomControl: false,
    fullscreenControl: false,
    gestureHandling: 'greedy',
  };

  const mapRef: MutableRefObject<google.maps.Map | null> = useRef(null);
  const hasInitialFitDoneRef = useRef(false);

  const fitBoundsToMarkers = useCallback(
    (map: google.maps.Map, markers: Marker[]) => {
      if (markers.length === 0) {
        return;
      }
      const bounds = new google.maps.LatLngBounds();
      markers.forEach((marker) => {
        bounds.extend(new google.maps.LatLng(marker.lat, marker.lng));
      });
      map.fitBounds(bounds);
      // fitBounds は非同期でアニメーションするため、idle イベントで完了後に state を更新する
      google.maps.event.addListenerOnce(map, 'idle', () => {
        setCurrentCenter(map.getCenter()?.toJSON() ?? center);
        setCurrentZoom(map.getZoom() ?? zoom);
      });
    },
    [center, zoom],
  );

  const tryInitialFitBounds = useCallback(
    (map: google.maps.Map) => {
      if (hasInitialFitDoneRef.current) return;
      const markers = markerPositions ?? [];
      if (markers.length > 0) {
        fitBoundsToMarkers(map, markers);
        hasInitialFitDoneRef.current = true;
      }
    },
    [markerPositions, fitBoundsToMarkers],
  );

  useEffect(() => {
    const map = mapRef.current;
    if (map) {
      tryInitialFitBounds(map);
    }
  }, [tryInitialFitBounds]);

  return (
    <GoogleMap
      mapContainerStyle={{ width: '100%', height: '100%' }}
      center={currentCenter}
      zoom={currentZoom}
      options={mapOptions}
      onClick={(event) => {
        onMarkerClick('');

        if ('placeId' in event) {
          const { placeId: placeIdValue } = event;
          setPlaceId(placeIdValue as PlaceId);
        } else {
          setPlaceId(null);
        }

        const { latLng } = event;
        if (latLng) {
          const lat = latLng.lat();
          const lng = latLng.lng();
          setCurrentPosition({ lat, lng });
        }
      }}
      onLoad={(map) => {
        mapRef.current = map;
        tryInitialFitBounds(map);
        map.addListener('zoom_changed', () => handleZoomChange(map.getZoom() ?? 0));
      }}
    >
      {renderMarkers?.()}
      {mapButton?.({ handleZoomChange, handleCenterChange })}
      {children}
      {isShow && currentPosition && (
        <InfoWindowF
          position={currentPosition}
          onDomReady={() => {
            setShow(false);
          }}
        >
          <div />
        </InfoWindowF>
      )}
    </GoogleMap>
  );
};

export default MyGoogleMap;
